import type { BenchmarkStatsByTag, BenchmarkTagSeriesStats } from '../../types/benchmark';
import { Tooltip } from '../ui/Tooltip';

/**
 * PROD1.7.8 N1 — 3 cards stats côte-à-côte : Global / Standards / Complexes.
 *
 * Distingue la performance du LLM sur les cas standards vs les cas
 * structurellement atypiques (tag `cas_complexe` en BD).
 *
 * Si `complexes === null` (aucun doc complexe dans le run), affiche une
 * card désactivée avec une mention dédiée pour distinguer "aucun cas
 * complexe" de "0% de réussite sur les cas complexes".
 */

const TAG_TOOLTIP = `Documents marqués \`cas_complexe\` en base : séquestre/faillite,
multi-immeubles, structures juridiques atypiques. Inclus ici pour ne pas
fausser la moyenne F1 sur les cas standards.

Voir DKI_SPRINT_PROD1.7.8.md section 3.1 pour le détail des patterns.`;

interface SeriesCardProps {
  title: string;
  series: BenchmarkTagSeriesStats | null;
  colorClass: string;
  withTooltip?: boolean;
  /** Message affiché quand la série est null (sous-population vide). */
  emptyLabel?: string;
}

function TitleWithOptionalTooltip({ title, withTooltip }: { title: string; withTooltip?: boolean }) {
  return (
    <div className="text-sm font-medium text-gray-700 mb-2 flex items-center">
      {title}
      {withTooltip && (
        <Tooltip
          content={TAG_TOOLTIP}
          heading="Tag cas_complexe"
          maxWidth="max-w-md"
          className="text-blue-600 ml-1"
          ariaLabel="Aide tag cas_complexe"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </Tooltip>
      )}
    </div>
  );
}

function SeriesCard({ title, series, colorClass, withTooltip, emptyLabel }: SeriesCardProps) {
  // Série null = sous-population vide (le backend renvoie null, pas 0%) → carte
  // désactivée avec un libellé dédié, pour les 3 séries de façon uniforme.
  if (!series) {
    return (
      <div className="p-4 border rounded-lg bg-gray-50 border-gray-200 border-dashed opacity-60">
        <TitleWithOptionalTooltip title={title} withTooltip={withTooltip} />
        <div className="text-sm text-gray-500 italic">
          {emptyLabel ?? 'Aucune donnée pour ce run'}
        </div>
      </div>
    );
  }
  return (
    <div className={`p-4 border rounded-lg ${colorClass}`}>
      <TitleWithOptionalTooltip title={title} withTooltip={withTooltip} />
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div>
          <div className="text-xs text-gray-500">F1</div>
          <div className="text-xl font-bold text-gray-900">{(series.avg_f1 * 100).toFixed(1)}%</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Précision</div>
          <div className="text-base font-semibold text-gray-700">{(series.avg_precision * 100).toFixed(1)}%</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Rappel</div>
          <div className="text-base font-semibold text-gray-700">{(series.avg_recall * 100).toFixed(1)}%</div>
        </div>
      </div>
      <div className="text-xs text-gray-600">
        {series.n_docs} doc{series.n_docs > 1 ? 's' : ''} • {series.perfect_documents} parfait{series.perfect_documents > 1 ? 's' : ''} • {series.critical_error_documents} critique{series.critical_error_documents > 1 ? 's' : ''}
      </div>
    </div>
  );
}

interface Props {
  stats: BenchmarkStatsByTag;
}

export function BenchmarkStatsByTagCards({ stats }: Props) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <SeriesCard
        title="Global"
        series={stats.global}
        colorClass="bg-gray-50 border-gray-200"
      />
      <SeriesCard
        title="Cas standards"
        series={stats.standards}
        colorClass="bg-green-50 border-green-200"
        emptyLabel="Aucun cas standard dans ce run"
      />
      <SeriesCard
        title="Cas complexes"
        series={stats.complexes}
        colorClass="bg-amber-50 border-amber-200"
        withTooltip
        emptyLabel="Aucun cas complexe dans ce run"
      />
    </div>
  );
}
